import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct, resource } = detail;

        // Regular variant changes use the same gallery. Keep it mounted so
        // mobile browsers do not reload images and run a second layout pass.
        if (!newProduct) {
          const media = resource?.featured_media;
          const mediaId = media?.id;
          if (mediaId) {
            this.previewVariantMedia(
              String(mediaId),
              media?.preview_image?.src ?? '',
              media?.preview_image?.src ?? '',
              media?.alt ?? ''
            );
          }
          return;
        }

        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * Gives variant changes immediate media feedback while the canonical
   * Section Rendering response is still in flight.
   * @param {string} mediaId
   * @param {string} [mediaUrl]
   * @param {string} [zoomUrl]
   * @param {string} [mediaAlt]
   */
  previewVariantMedia(mediaId, mediaUrl = '', zoomUrl = '', mediaAlt = '') {
    const targetMedia = Array.from(this.querySelectorAll('.product-media[data-media-id]')).find(
      (media) => media instanceof HTMLElement && media.dataset.mediaId === mediaId
    );
    if (!(targetMedia instanceof HTMLElement)) {
      this.#applyRemoteVariantPreview(mediaId, mediaUrl, zoomUrl, mediaAlt);
      return;
    }

    this.dataset.variantPreviewMediaId = mediaId;

    const targetSlide = targetMedia.closest('slideshow-slide');
    const slideId = targetSlide?.getAttribute('slide-id');
    if (slideId && this.slideshow) {
      this.slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // Desktop grid galleries are re-ordered by the server response. Until it
    // arrives, promote the already-rendered high-resolution variant image into
    // the lead media slot without waiting for another image request.
    const leadMedia = this.querySelector('.product-media[data-media-id]');
    if (!(leadMedia instanceof HTMLElement) || leadMedia === targetMedia) return;

    const leadImage = leadMedia.querySelector('.product-media__image');
    const targetImage = targetMedia.querySelector('.product-media__image');
    if (!(leadImage instanceof HTMLImageElement) || !(targetImage instanceof HTMLImageElement)) return;

    /** @param {Element} first @param {Element} second @param {string} name */
    const swapAttribute = (first, second, name) => {
      const firstValue = first.getAttribute(name);
      const secondValue = second.getAttribute(name);
      secondValue == null ? first.removeAttribute(name) : first.setAttribute(name, secondValue);
      firstValue == null ? second.removeAttribute(name) : second.setAttribute(name, firstValue);
    };

    // Swap rather than copy so a rapid second click can immediately restore or
    // promote another variant before the server response completes.
    for (const attribute of ['sizes', 'srcset', 'src', 'alt', 'data-max-resolution', 'style']) {
      swapAttribute(leadImage, targetImage, attribute);
    }
    swapAttribute(leadMedia, targetMedia, 'data-media-id');
    swapAttribute(leadMedia, targetMedia, 'style');
    this.dataset.variantPreviewMediaId = mediaId;
  }

  /**
   * `hide_variants` intentionally removes unselected color images from the
   * gallery HTML. Use the URL embedded in the selected variant to update every
   * representation of the lead image (mobile carousel, desktop grid and zoom)
   * without waiting for a full gallery replacement.
   * @param {string} mediaId
   * @param {string} mediaUrl
   * @param {string} zoomUrl
   * @param {string} mediaAlt
   */
  #applyRemoteVariantPreview(mediaId, mediaUrl, zoomUrl, mediaAlt) {
    if (!mediaUrl) return;

    const allMedia = Array.from(this.querySelectorAll('.product-media[data-media-id]')).filter(
      (media) => media instanceof HTMLElement
    );
    const previousMediaId = this.dataset.variantPreviewMediaId || allMedia[0]?.dataset.mediaId || '';
    const leadRepresentations = allMedia.filter((media) => media.dataset.mediaId === previousMediaId);
    if (leadRepresentations.length === 0) return;

    for (const media of leadRepresentations) {
      const image = media.querySelector('.product-media__image');
      if (!(image instanceof HTMLImageElement)) continue;

      media.dataset.mediaId = mediaId;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = mediaUrl;
      image.dataset.maxResolution = zoomUrl || mediaUrl;
      if (mediaAlt) image.alt = mediaAlt;
    }

    this.dataset.variantPreviewMediaId = mediaId;

    const leadSlide = leadRepresentations.find((media) => media.closest('slideshow-slide'))?.closest('slideshow-slide');
    const slideId = leadSlide?.getAttribute('slide-id');
    if (slideId && this.slideshow) {
      this.slideshow.select({ id: slideId }, undefined, { animate: false });
    }
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
